import { Candidate, CandidateComment, CandidateManualEvaluation, CandidateStatus, Role } from "../Models";
import { APIURL, paginationData } from "./config";

export async function GetCandidateForRole(
  roleId: string,
  token: string,
  filterQuery = "",
  pageNumber = 1,
  searchQuery = "",
  sortQuery = "",
  getAllCandidates = false
) {
  const res = await fetch(
    `${APIURL}/Roles/${roleId}/Candidates?pageNumber=${pageNumber}&pageSize=${100}&filterQuery=${filterQuery}&searchQuery=${searchQuery}&orderBy=${sortQuery}&getAllCandidates=${getAllCandidates}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const candidates: {
    candidates: Candidate[];
    paginationMetadata: paginationData;
  } = await res.json();

  return candidates;
}

/**
 * Result of resolving an uploaded email list against a role's candidates. `candidates` is the
 * matched subset in the same shape the candidate grid already renders; every other field explains
 * which addresses did not become a row.
 */
export interface CandidateEmailImportResult {
  fileName: string | null;
  totalRows: number;
  hasHeaderRow: boolean;
  emailColumnIndex: number;
  columns: string[];
  previewRows: string[][];
  truncated: boolean;
  emailsParsed: number;
  duplicateCount: number;
  invalidCount: number;
  duplicateEmails: string[];
  invalidValues: string[];
  emailsNotOnRole: string[];
  emailsNotOnRoleCount: number;
  emailsNotFound: string[];
  emailsNotFoundCount: number;
  candidates: Candidate[];
  paginationMetadata: paginationData | null;
}

/**
 * Reads an .xlsx/.csv/.tsv/.txt email list (or a pasted block of addresses) and returns this
 * role's candidates for those emails. Read-only — nothing is advanced or modified. The uploaded
 * list replaces the sidebar filter server-side, so an unmatched address really means the person
 * is not a candidate on this role.
 */
export async function ResolveCandidatesByEmailList(
  roleId: string,
  token: string,
  input: { file?: File; emails?: string; emailColumnIndex?: number },
  sortQuery = ""
): Promise<CandidateEmailImportResult> {
  const form = new FormData();
  if (input.file) form.append("file", input.file);
  if (input.emails) form.append("emails", input.emails);
  if (input.emailColumnIndex !== undefined)
    form.append("emailColumnIndex", String(input.emailColumnIndex));

  const res = await fetch(
    `${APIURL}/Roles/${roleId}/Candidates/ByEmails?orderBy=${encodeURIComponent(sortQuery)}`,
    {
      method: "POST",
      // Content-Type is deliberately unset so the browser adds the multipart boundary.
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message ?? "Failed to read the email list.");
  }

  return await res.json();
}

export async function GetCandidate(candidateId: string, token: string) {
  const res = await fetch(`${APIURL}/Candidates/${candidateId}/full`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const candidate: Candidate = await res.json();

  return candidate;
}

export async function GetRoleForCandidate(candidateId: string, token: string) {
  const res = await fetch(`${APIURL}/Candidates/${candidateId}/roles`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const role: Role[] = await res.json();
  return role;
}

export async function GetCandidateStatus(candidateId: string, token: string) {
  const res = await fetch(`${APIURL}/Candidates/${candidateId}/Status`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const CandidateStatus: CandidateStatus[] = await res.json();
  return CandidateStatus;
}

export async function CreateCandidateStatus(
  token: string,
  body: CandidateStatus
) {
  const res = await fetch(`${APIURL}/Candidates/Status`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    throw new Error(errorBody?.message || "Failed to update candidate status");
  }
  const CandidateStatus: CandidateStatus = await res.json();
  return CandidateStatus;
}

export async function CreateMultipleCandidateStatus(
  token: string,
  body: CandidateStatus[]
) {
  const res = await fetch(`${APIURL}/Candidates/Status/multiple`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    const detail = errorBody?.errors?.map((e: any) => e.message).join("; ");
    throw new Error(detail || errorBody?.message || "Failed to update candidate statuses");
  }
  const json = await res.json();
  // The endpoint now returns { statuses, emailSummary }. Tolerate the previous bare-array
  // shape so a stale API deployment cannot break advancing candidates.
  const statuses: CandidateStatus[] = Array.isArray(json) ? json : json?.statuses ?? [];
  const emailSummary: EmailSendSummary | null = Array.isArray(json)
    ? null
    : json?.emailSummary ?? null;
  return { statuses, emailSummary };
}

export interface FailedEmailRecipient {
  candidateId: string;
  name: string;
  email: string;
  slotId: string;
  reason?: string;
}

export interface EmailSendSummary {
  sent: number;
  failed: number;
  failedCandidates: FailedEmailRecipient[];
}

/**
 * Re-sends the assessment slot confirmation (SEB) email to candidates whose mail failed
 * during a bulk advance. Purely a resend — no booking or status is modified.
 */
export async function ResendSlotConfirmation(
  token: string,
  candidateIds: string[],
  assessmentSlotId: string
): Promise<EmailSendSummary> {
  const res = await fetch(`${APIURL}/Candidates/Status/resendSlotConfirmation`, {
    method: "POST",
    body: JSON.stringify({ CandidateIds: candidateIds, AssessmentSlotId: assessmentSlotId }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    throw new Error(errorBody?.message || "Failed to resend assessment slot emails");
  }
  return res.json();
}

export async function GetCandidateComments(candidateId: string, token: string) {
  const res = await fetch(`${APIURL}/Candidates/${candidateId}/comment`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const CandidateComments: CandidateComment[] = await res.json();
  return CandidateComments;
}

export async function CreateCandidateComment(
  body: CandidateComment,
  token: string
) {
  const res = await fetch(`${APIURL}/Candidates/comment`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const newCandidateComment: CandidateComment = await res.json();
  return newCandidateComment;
}

export async function DeleteComment(id: string, token: string) {
  await fetch(`${APIURL}/Candidates/comment/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function GetCandidateTimeline(
  roleId: string,
  candidateId: string,
  token: string
) {
  const res = await fetch(
    `${APIURL}/Candidates/${roleId}/candidate/${candidateId}/timeline`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const timeline = await res.json();
  return timeline;
}

export async function GetCandidateCv(candidateId: string, token: string) {
  const res = await fetch(`${APIURL}/Candidates/candidate/${candidateId}/cv`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const cvBlob = await res.blob();
  return cvBlob;
}

export const GetCandidateApplies = async (roleId: string, email: string, token: string) => {
  const res = await fetch(`${APIURL}/Candidates/${roleId}/Applies/${email}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const candidates: { name: string; email: string }[] = await res.json();
  return candidates;
};

export const updateCandidateMetrics = async (roleId: string, token: string) => {
  const url = `${APIURL}/Candidates/Metrics/${roleId}`;
  try {
    await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    console.error("Error updating metrics:", err);
  }
};

export const getUngradedRoles = async (token: string) => {
  const res = await fetch(`${APIURL}/Candidates/ungradedroles`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json: string[] = await res.json();
  return json;
};

export const getAnsweredPercentage = async (cId: string, roleId: string, token: string) => {
  const res = await fetch(
    `${APIURL}/Candidates/${cId}/AnsweredPercentage/${roleId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const json: {
    answeredPercentage: number;
    startTime?: string;
    endTime?: string;
  } = await res.json();
  return json;
};

export const GetCandidateScore = async (
  id: string,
  token: string,
  roleId?: string
) => {
  const res = await fetch(
    `${APIURL}/Candidates/${id}/score${roleId ? `?roleId=${roleId}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const result = await res.json();
  return result;
};

export const createInterviewSession = async (assessmentId: string, candidateId: string, roleId: string, slotId: string, token: string) => {
  const res = await fetch(`${APIURL}/Candidates/interviewSlot/AddAssessment`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      assessmentQuestionnaireId: assessmentId,
      candidateId: candidateId,
      roleId: roleId,
      slotId: slotId,
    }),
  });
  const json = await res.json();
  return json;
}

export const GetCandidateInterviews = async (
  candidateId: string,
  roleId: string,
  token: string
) => {
  try {
    const res = await fetch(
      `${APIURL}/Candidates/${candidateId}/Interview/${roleId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) {
      console.error(`Error fetching candidate interviews: ${res.status} ${res.statusText}`);
      return null;
    }
    const result = await res.json();
    return result;
  } catch (error) {
    console.error("Error fetching candidate interviews:", error);
    return null;
  }
};

export const GetExtractedCVData = async (candidateId: string, token: string) => {
  const res = await fetch(`${APIURL}/Candidates/GetExtractedCV/${candidateId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
}

export const GetCandidateSortedEducation = async (cId: string, token: string) => {
  const res = await fetch(`${APIURL}/Candidates/${cId}/Experience`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
}

export const updateCVErrors =
  async (candidateId: string, token: string, IsEducationDetailsMismatch: boolean, IsEducationPeriodMismatch: boolean, IsExperienceDetailsMismatch: boolean, IsExperiencePeriodMismatch: boolean, IsCVDataMismatch: boolean) => {
    await fetch(`${APIURL}/Candidates/UpdateCVErrors/${candidateId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(
        {
          "isEducationDetailsMismatch": IsEducationDetailsMismatch,
          "isEducationPeriodMismatch": IsEducationPeriodMismatch,
          "isExperienceDetailsMismatch": IsExperienceDetailsMismatch,
          "isExperiencePeriodMismatch": IsExperiencePeriodMismatch,
          "isCVDataMismatch": IsCVDataMismatch
        }
      ),
    });
  }

export const ResetCandidateScore = async (
  candidateId: string,
  token: string,
  roleAssessmentMappingId?: string
) => {
  const url = roleAssessmentMappingId
    ? `${APIURL}/Candidates/${candidateId}/ResetScore?roleAssessmentMappingId=${roleAssessmentMappingId}`
    : `${APIURL}/Candidates/${candidateId}/ResetScore`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Failed to reset candidate score(s)");
  }
  return response.json();
};

export const BookInterviewSlot = async (
  slotId: string,
  candidateId: string,
  notes: string | null,
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/bookInterviewSlot`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      slotId,
      candidateId,
      notes,
    }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to book interview slot");
  }

  return await res.json();
};

export const SubmitManualEvaluation = async (
  evaluation: {
    candidateId: string;
    roleId: string;
    roleStatusId: string;
    /**
     * null means "don't know the current rating, leave it as-is" (used when setting the
     * score alone, without an existing rating cached locally). "" is an explicit clear
     * (toggling a selected grade button off) and still overwrites, same as any other
     * value. See CandidatesController.CreateManualEvaluation for the matching backend
     * side of this.
     */
    rating: string | null;
    score?: number;
    comments?: string;
  },
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/ManualEvaluation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(evaluation),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to submit manual evaluation");
  }

  return await res.json();
};

export const GetCandidateManualEvaluations = async (
  candidateId: string,
  token: string,
  roleId?: string
) => {
  const url = roleId
    ? `${APIURL}/Candidates/${candidateId}/ManualEvaluations?roleId=${roleId}`
    : `${APIURL}/Candidates/${candidateId}/ManualEvaluations`;

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error("Failed to fetch manual evaluations");
  }

  const evaluations: CandidateManualEvaluation[] = await res.json();
  return evaluations;
};

export const DeleteManualEvaluation = async (
  evaluationId: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/ManualEvaluation/${evaluationId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error("Failed to delete manual evaluation");
  }

  return;
};

export const getCandidateVideoAnswers = async (
  cIds: string[],
  token: string
): Promise<any[]> => {
  const res = await fetch(
    `${APIURL}/CandidateMediaAnswer/Answers/Multiple/Video`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(cIds),
    }
  );
  const json = await res.json();
  return json;
};

export const postCandidateSpecificQuestionnaire = async (
  candidateIds: string[],
  assessmentQuestionnaireId: string,
  roleId: string,
  token: string
) => {
  await fetch(
    `${APIURL}/CandidateSpecificQuestionnaires?assessmentQuestionnaireId=${assessmentQuestionnaireId}&RoleId=${roleId}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(candidateIds),
    }
  );
};

export const GetQuestionAnsweredByCandidate = async (
  raId: string,
  candidateId: string,
  token: string
) => {
  const answersResponse = await fetch(
    `${APIURL}/CandidateAnswer/${candidateId}/Assessment/${raId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!answersResponse.ok) throw new Error("Failed to fetch candidate data");
  const answersData = await answersResponse.json();
  return answersData;
};


export async function GetCandidateInterviewAnswers(
  candidateId: string,
  interviewId: string,
) {
  const res = await fetch(
    `${APIURL}/CandidateInterviewSlot/questions/${interviewId}/${candidateId}`,
  )
  if (!res.ok) {
    return null;
  }
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return await res.json();
  }
  return null;
}

export async function GradeCandidateInterview(
  candidateInterviewSlowId: string,
  assessmentQuestionId: string,
  manualEvaluation: number,
  token: string,
) {
  const res = await fetch(
    `${APIURL}/CandidateInterviewSlot/grade`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        candidateInterviewSlotId: candidateInterviewSlowId,
        assessmentQuestionId: assessmentQuestionId,
        manualEvaluation: manualEvaluation,
      }),
    }
  )
  const json = await res.json();
  return json;
}

export async function GetInterviewSlotDetails(
  candidateId: string,
  roleId: string,
  slotId: string,
) {
  const res = await fetch(
    `${APIURL}/CandidateInterviewSlot/candidate/${candidateId}/role/${roleId}/slot/${slotId}`,
  )
  if (!res.ok) {
    return null;
  }
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return await res.json();
  }
  return null;
}

//call for mapping filters

export async function getAllRoles() {
  const res = await fetch(`${APIURL}/Roles/GetAllRoles`, {

    headers: {
      "Content-Type": "application/json",
    },
  });
  const roles: Role[] = await res.json();
  return roles;
}

export async function getAllAssessmentSlots(
  filterQuery: string = "",
  searchQuery: string = "",
  dateFilter: string = "",
  token: string
) {
  const params = new URLSearchParams();
  if (filterQuery) params.append("filterQuery", filterQuery);
  if (searchQuery) params.append("searchQuery", searchQuery);
  if (dateFilter) params.append("dateFilter", dateFilter);

  const url = `${APIURL}/Roles/GetAllAssessmentSlots${params.toString() ? `?${params.toString()}` : ""}`;
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const assessmentSlots = await res.json();
  return assessmentSlots;
}
export const getAssessmentSlotStatus = async (candidateId: string, assessmentSlotId: string, token: string) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/${candidateId}/${assessmentSlotId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to fetch status");
    const json = await response.json();
    return json;
  } catch (error) {
    console.error('Error fetching AssessmentSlotStatus:', error);
    return { status: 'paused', statusCode: 0 };
  }
};
// to update status for all candidate
export const updateAssessmentSlotStatus = async (assessmentSlotId: string, statusCode: number, token: string) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/updateAll`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          AssessmentSlotId: assessmentSlotId,
          AdminStatusCode: statusCode,
        }),
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error:', error);
    alert('Failed to update status');
  }
};

export const updateSingleCandidateAssessmentSlotStatus = async (
  candidateId: string,
  assessmentSlotId: string,
  statusCode: number,
  token: string
) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/updateSingle`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CandidateId: candidateId,
          AssessmentSlotId: assessmentSlotId,
          AdminStatusCode: statusCode,
        }),
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error:', error);
    alert('Failed to update candidate status');
  }
};

export const updateBulkCandidateAssessmentSlotStatus = async (
  candidateIds: string[],
  assessmentSlotId: string,
  statusCode: number,
  token: string
) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/updateBulk`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CandidateIds: candidateIds,
          AssessmentSlotId: assessmentSlotId,
          AdminStatusCode: statusCode,
        }),
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error:', error);
    alert('Failed to update candidates status');
  }
};

export const getAvailableSlotsForReassign = async (roleStatusId: string, token: string) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/availableSlots/${roleStatusId}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching available slots:', error);
    return [];
  }
};

export const reassignCandidateSlot = async (
  candidateId: string,
  currentSlotId: string | null,
  newSlotId: string | null,
  cancelCurrentSlot: boolean,
  bookNewSlot: boolean,
  token: string
) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/reassign`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CandidateId: candidateId,
          CurrentSlotId: currentSlotId,
          NewSlotId: newSlotId,
          CancelCurrentSlot: cancelCurrentSlot,
          BookNewSlot: bookNewSlot,
        }),
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error reassigning slot:', error);
    throw error;
  }
};
//reset an assement 
export const resetCandidateAssessmentScore = async (
  candidateId: string,
  roleStatusId: string,
  token: string
) => {
  try {
    const response = await fetch(
      `${APIURL}/AssessmentSlotStatus/resetCandidateAssessmentScore`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CandidateId: candidateId,
          RoleStatusId: roleStatusId,
        }),
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error resetting candidate assessment score:', error);
    throw error;
  }
};



export async function getCandidatesBySlot(
  slotId: string,
  filterQuery: string = "",
  searchQuery: string = "",
  token: string
) {
  const params = new URLSearchParams();
  if (filterQuery) params.append("filterQuery", filterQuery);
  if (searchQuery) params.append("searchQuery", searchQuery);

  const url = `${APIURL}/Roles/getCandidatesBySlot/${slotId}${params.toString() ? `?${params.toString()}` : ""}`;
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await res.json();
  return data;
}


export const UnbookAssessmentSlot = async (slotId: string, token: string) => {
  const res = await fetch(`${APIURL}/Roles/unbookAssessmentSlot/${slotId}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to unbook assessment slot");
  }

  return await res.json();
};

// Fetch all assessment slots booked by a candidate
export const GetCandidateAssessmentSlots = async (
  candidateId: string,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/Candidates/${candidateId}/assessmentSlots`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!res.ok) {
    return null;
  }
  const result = await res.json();
  return result;
};

export const BookAssessmentSlot = async (
  slotId: string,
  candidateId: string,
  notes: string | null,
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/bookAssessmentSlot`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ SlotId: slotId, CandidateId: candidateId, Notes: notes }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to book assessment slot");
  }

  return await res.json();
};

export const AppliedRolesForCandidate = async (
  candidateId: string,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/Candidates/${candidateId}/AppliedRoles`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!res.ok) return [];
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return await res.json();
  }
  return [];
};

export const GetCandidateRoleTimeline = async (roleId: string, candidateId: string, token: string) => {
  const res = await fetch(
    `${APIURL}/Roles/${roleId}/timeline/${candidateId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!res.ok) {
    return { timeline: [], eventTypes: {} };
  }
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return await res.json();
  }
  return { timeline: [], eventTypes: {} };
}



export const SendCustomEmail = async (
  candidateIds: string[],
  subject: string,
  body: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/SendCustomEmail`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ candidateIds, subject, body }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to send custom email");
  }

  return await res.json();
};

export type CandidateCvFlag = {
  flag: boolean;
  flag_title: string;
  reason: string;
};

export type CandidateCvFlagResult = {
  flags: CandidateCvFlag[];
  tier?: string;
  score?: number;
  summary?: string;
};

export const GetCandidateCvFlagResult = async (
  roleId: string,
  candidateId: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/AI/FlagCV/${roleId}/${candidateId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to fetch candidate CV flags");
  }

  const json: CandidateCvFlagResult = await res.json();
  return json;
};

export const GetStoredCandidateCvFlagResult = async (
  roleId: string,
  candidateId: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/AI/FlagCV/${roleId}/${candidateId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to fetch stored candidate CV flags");
  }

  const json: CandidateCvFlagResult = await res.json();
  return json;
};

export const GenerateCandidateCvFlagResult = async (
  roleId: string,
  candidateId: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/AI/FlagCV/${roleId}/${candidateId}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to generate candidate CV flags");
  }

  const json: CandidateCvFlagResult = await res.json();
  return json;
};

// -----------------------------------------------------------------------
// OpenAI Batch API – flag CVs for multiple candidates in one shot
// -----------------------------------------------------------------------

export interface CandidateFlagResult {
  candidateId: string;
  flagResult?: CandidateCvFlagResult;
  error?: string;
}

export interface BatchFlagStatusResponse {
  status: string; // "validating" | "in_progress" | "completed" | "failed" | "expired" | "cancelling" | "cancelled"
  results?: CandidateFlagResult[];
}

export const SubmitFlagCVBatch = async (
  roleId: string,
  candidateIds: string[],
  token: string
): Promise<{ batchId: string; message: string }> => {
  const res = await fetch(`${APIURL}/AI/FlagCV/${roleId}/batch`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ candidateIds }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to submit CV flag batch");
  }

  return res.json();
};

export const GetFlagCVBatchStatus = async (
  batchId: string,
  token: string
): Promise<BatchFlagStatusResponse> => {
  const res = await fetch(`${APIURL}/AI/FlagCV/batch/${batchId}/status`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to get CV flag batch status");
  }

  return res.json();
};

export const GetFlagCVPromptPreview = async (
  roleId: string,
  token: string
): Promise<{ prompt: string }> => {
  const res = await fetch(`${APIURL}/AI/FlagCV/${roleId}/prompt-preview`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to get CV flag prompt preview");
  }

  return res.json();
};

export const GetResetJourneyEligibility = async (
  candidateId: string,
  roleId: string,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/Candidates/${candidateId}/resetJourneyEligibility?roleId=${roleId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) {
    throw new Error("Failed to get reset journey eligibility");
  }

  return res.json();
};

export const ResetCandidateJourney = async (
  candidateId: string,
  roleId: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/${candidateId}/resetJourney`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ roleId }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to reset candidate journey");
  }

  return res.json();
};
